import * as React from "react";
import { configureApi } from "@/lib/apiClientRuntime";
import { setApiScope } from "@/lib/apiPath";
import { createPublicSession, fetchPublicCompanySettings } from "./publicSessionService";
import { widgetI18n } from "./lib/i18n/widgetI18n";
import {
  WIDGET_LANGUAGE_STORAGE_KEY,
  changeLanguageAndPersist,
  normalizeLanguageCode,
  readLanguageCookie,
  readQueryLanguage,
} from "@/lib/i18n/languageSettings";
import type { CompanySettings } from "@/lib/types/company";

function updateWidgetDocumentTitle(settings: CompanySettings): void {
  if (typeof document === "undefined") return;

  const title = [settings.widget_title, settings.public_title]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ");

  if (title) {
    document.title = title;
  }
}

export function usePublicBootstrap(opts: { apiBase?: string; widgetHost: string }) {
  const { apiBase = (import.meta.env.VITE_API_URL ?? "https://boona.app"), widgetHost } = opts;
  const [ready, setReady] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);
  const [companyId, setCompanyId] = React.useState<number | null>(null);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const hasLanguageCookie = Boolean(readLanguageCookie());
        const queryLanguage = readQueryLanguage();

        // 1. Check local storage for cached region to optimize initial connection
        const cachedRegion = localStorage.getItem("widget_region");
        if (cachedRegion && cachedRegion !== "default") {
          configureApi(`https://api-${cachedRegion}.boona.app`);
        } else {
          configureApi(apiBase);
        }
        // Ensure public scope so apiPath('/company/settings') resolves to /api/v1/public/...
        setApiScope("public");

        // 2. Create session with widget_host; backend resolves to company_id
        const { companyId: resolvedCompanyId, region } = await createPublicSession(widgetHost);

        // 3. Switch to regional API if backend returned a region
        if (region) {
          localStorage.setItem("widget_region", region);
          if (region !== "default") {
            configureApi(`https://api-${region}.boona.app`);
          }
        }

        // 4. Fetch company settings (now targeting the correct regional API)
        const settings = await fetchPublicCompanySettings();
        updateWidgetDocumentTitle(settings);

        if (!hasLanguageCookie) {
          if (queryLanguage) {
            await changeLanguageAndPersist(widgetI18n, queryLanguage, WIDGET_LANGUAGE_STORAGE_KEY);
          } else {
            const companyLanguage = normalizeLanguageCode(settings.locale_short);
            if (companyLanguage) {
              await changeLanguageAndPersist(widgetI18n, companyLanguage, WIDGET_LANGUAGE_STORAGE_KEY);
            }
          }
        }

        if (alive) {
          setCompanyId(resolvedCompanyId);
          setReady(true);
        }
      } catch (e) {
        if (alive) setError(e as Error);
      }
    })();
    return () => {
      alive = false;
    };
  }, [apiBase, widgetHost]);

  return { ready, error, companyId };
}
